import {
  QueryV1,
  BoolQueryV1,
  MultiMatchQueryV1,
  RangeQueryV1,
  Fuzziness,
  FuzzinessAuto
} from '@ttab/elephant-api/index'
import { fromZonedTime } from 'date-fns-tz'
import type { AdvancedSearchState, DateGranularity, DateRangeTarget, SearchFieldConfig } from '../types'

function escapeRegExp(str: string): string {
  return str.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
}

/**
 * Replace user-friendly field aliases with actual OpenSearch field paths
 * in a query syntax string. E.g. `title:"ukraine"` -> `document.title:"ukraine"`
 * Matches whole names only: neither 'subtitle:' nor 'document.title:' takes the 'title' alias.
 */
function replaceFieldAliases(raw: string, fields: SearchFieldConfig[]): string {
  let result = raw
  for (const field of fields) {
    const pattern = new RegExp(`(?<![\\w.])${escapeRegExp(field.syntaxAlias)}:`, 'g')
    result = result.replace(pattern, `${field.fieldPath}:`)
  }
  return result
}

// Day after a YYYY-MM-DD date. Kept on the UTC calendar in both directions so
// the result does not shift with the timezone of the machine building the query.
function nextDay(date: string): string {
  const [year, month, day] = date.split('-').map(Number)
  const next = new Date(Date.UTC(year, month - 1, day + 1))
  return next.toISOString().slice(0, 10)
}

// A bare date reaches OpenSearch as midnight UTC. That is the right boundary for a
// field storing bare dates, and off by the zone's offset for one storing instants,
// which needs the moment the day actually starts in the configured timezone.
function rangeBound(date: string, granularity: DateGranularity, timeZone: string): string {
  return granularity === 'day'
    ? date
    : fromZonedTime(`${date}T00:00:00`, timeZone).toISOString()
}

export function buildAdvancedQuery(
  state: AdvancedSearchState,
  fields: SearchFieldConfig[],
  dateTarget?: DateRangeTarget
): QueryV1 | undefined {
  if (state.mode === 'querySyntax') {
    const raw = state.querySyntax.raw.trim()
    if (!raw) {
      return undefined
    }

    return QueryV1.create({
      conditions: {
        oneofKind: 'queryString',
        queryString: replaceFieldAliases(raw, fields)
      }
    })
  }

  const s = state.structured
  const conditions: QueryV1[] = []

  // Text query
  const query = s.query.trim()
  if (query) {
    const chosen = s.selectedFields.length > 0
      ? s.selectedFields
      : fields.filter((f) => f.defaultSelected).map((f) => f.fieldPath)

    // A field required to be missing cannot also hold the term, so searching it
    // would only ever subtract hits. Dropping it keeps the two halves compatible.
    // If that leaves nothing the request is contradictory as asked, and is sent
    // unchanged so it returns nothing rather than quietly matching something else.
    const missing = new Set(s.fieldExists.filter((fe) => !fe.exists).map((fe) => fe.field))
    const searchable = chosen.filter((f) => !missing.has(f))
    const selectedFields = searchable.length > 0 ? searchable : chosen

    const multiMatch = MultiMatchQueryV1.create({
      fields: selectedFields,
      query,
      type: s.matchType,
      booleanAnd: s.booleanAnd,
      boost: s.boost > 1 ? s.boost : 0
    })

    // OpenSearch rejects multi_match type=phrase with fuzziness, so skip fuzzy for phrase queries.
    if (s.fuzzy && s.matchType !== 'phrase') {
      if (s.fuzzyEdits === 'auto') {
        multiMatch.fuzziness = Fuzziness.create({
          auto: FuzzinessAuto.create({ low: BigInt(0), high: BigInt(0) })
        })
      } else {
        multiMatch.fuzziness = Fuzziness.create({
          edits: BigInt(s.fuzzyEdits)
        })
      }
      if (s.fuzzyPrefixLength > 0) {
        multiMatch.prefixLength = BigInt(s.fuzzyPrefixLength)
      }
    }

    conditions.push(QueryV1.create({
      conditions: { oneofKind: 'multiMatch', multiMatch }
    }))
  }

  // Date range - use lt with next day so the whole end date is included
  if (dateTarget && (s.dateRange.from || s.dateRange.to)) {
    const { field, timeZone } = dateTarget
    const range = RangeQueryV1.create({ field: field.path })
    if (s.dateRange.from) {
      range.gte = rangeBound(s.dateRange.from, field.granularity, timeZone)
    }
    if (s.dateRange.to) {
      range.lt = rangeBound(nextDay(s.dateRange.to), field.granularity, timeZone)
    }
    conditions.push(QueryV1.create({
      conditions: { oneofKind: 'range', range }
    }))
  }

  // Field exists / missing
  for (const fe of s.fieldExists) {
    const existsQuery = QueryV1.create({
      conditions: { oneofKind: 'exists', exists: fe.field }
    })
    if (fe.exists) {
      conditions.push(existsQuery)
    } else {
      conditions.push(QueryV1.create({
        conditions: {
          oneofKind: 'bool',
          bool: BoolQueryV1.create({ mustNot: [existsQuery] })
        }
      }))
    }
  }

  if (conditions.length === 0) {
    return undefined
  }

  if (conditions.length === 1) {
    return conditions[0]
  }

  return QueryV1.create({
    conditions: {
      oneofKind: 'bool',
      bool: BoolQueryV1.create({ must: conditions })
    }
  })
}
